import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import aiModelService from "services/aiModels.js";
import logger from "utils/logger";

export default function CardAiModelTable({ onEditModel, onDeleteModel, onNewModel }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadModels = async () => {
    try {
      setLoading(true);
      const response = await aiModelService.getModels();
      setModels(response.data || []);
      setError(null);
    } catch (err) {
      logger.error('Error loading AI models:', err);
      setError('Error al cargar los modelos de IA');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModels();
  }, []);

  const handleToggleActive = async (model) => {
    setTogglingId(model.id);
    try {
      await aiModelService.updateModel(model.id, { active: !model.active });
      await loadModels();
    } catch (err) {
      logger.error('Error toggling AI model active state:', err);
      alert('Error al cambiar el estado del modelo');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (model) => {
    if (window.confirm(`¿Estás seguro de que quieres eliminar el modelo "${model.name}"?`)) {
      try {
        await aiModelService.deleteModel(model.id);
        await loadModels();
        if (onDeleteModel) onDeleteModel(model.id);
      } catch (err) {
        logger.error('Error deleting AI model:', err);
        alert('Error al eliminar el modelo de IA');
      }
    }
  };

  return (
    <div className="relative flex flex-col min-w-0 break-words w-full shadow-lg rounded bg-white">
      <div className="rounded-t mb-0 px-4 py-3 border-0">
        <div className="flex flex-wrap items-center">
          <div className="relative w-full px-4 max-w-full flex-grow flex-1">
            <h3 className="font-semibold text-lg text-blueGray-700">
              Modelos de IA
            </h3>
          </div>
          <div className="relative w-full px-4 max-w-full flex-grow flex-1 text-right">
            {onNewModel && (
              <button
                className="bg-indigo-500 text-white active:bg-indigo-600 text-xs font-bold uppercase px-3 py-1 rounded outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={onNewModel}
              >
                Nuevo Modelo
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="block w-full overflow-x-auto">
        <table className="items-center w-full bg-transparent border-collapse">
          <thead>
            <tr>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                ID
              </th>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                Nombre
              </th>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                Identificador API
              </th>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                Proveedor
              </th>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                Activo
              </th>
              <th className="px-3 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-center bg-blueGray-50 text-blueGray-500 border-blueGray-100">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="6" className="px-3 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
                  </div>
                </td>
              </tr>
            )}

            {error && (
              <tr>
                <td colSpan="6" className="px-3 py-4 text-center text-red-500">
                  {error}
                </td>
              </tr>
            )}

            {!loading && !error && models.length === 0 && (
              <tr>
                <td colSpan="6" className="px-3 py-4 text-center text-gray-500">
                  No hay modelos de IA registrados
                </td>
              </tr>
            )}

            {!loading && !error && models.map((model) => (
              <tr key={model.id}>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                  <span className="text-blueGray-600 font-semibold">
                    {model.id}
                  </span>
                </td>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs p-2">
                  <span className="font-semibold text-blueGray-600">
                    {model.name}
                  </span>
                </td>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs p-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-mono bg-blueGray-100 text-blueGray-700">
                    {model.api_identifier}
                  </span>
                </td>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                  {model.provider ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-teal-100 text-teal-800">
                      {model.provider}
                    </span>
                  ) : (
                    <span className="text-blueGray-400 italic">-</span>
                  )}
                </td>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                  <button
                    onClick={() => handleToggleActive(model)}
                    disabled={togglingId === model.id}
                    title={model.active ? 'Desactivar modelo' : 'Activar modelo'}
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      alignItems: 'center',
                      width: '2.5rem',
                      height: '1.25rem',
                      borderRadius: '9999px',
                      border: 'none',
                      cursor: togglingId === model.id ? 'not-allowed' : 'pointer',
                      backgroundColor: model.active ? '#10b981' : '#d1d5db',
                      transition: 'background-color 0.2s',
                      opacity: togglingId === model.id ? 0.6 : 1,
                      padding: 0,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: '1rem',
                        height: '1rem',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                        transform: model.active ? 'translateX(1.25rem)' : 'translateX(0.125rem)',
                        transition: 'transform 0.2s',
                      }}
                    />
                  </button>
                </td>
                <td className="border-t-0 align-middle border-l-0 border-r-0 text-xs whitespace-nowrap p-2 text-center">
                  <div className="flex space-x-1 justify-center">
                    <button
                      className="text-indigo-600 hover:text-indigo-900 text-xs font-semibold px-2"
                      onClick={() => onEditModel && onEditModel(model)}
                      title="Editar modelo"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="text-red-600 hover:text-red-900 text-xs font-semibold"
                      onClick={() => handleDelete(model)}
                      title="Eliminar modelo"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

CardAiModelTable.propTypes = {
  onEditModel: PropTypes.func,
  onDeleteModel: PropTypes.func,
  onNewModel: PropTypes.func,
};
